import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import React from 'react';
import { useEpdProductComponents, useGetLCASpecification } from 'services/api/queries';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { EPDContentDeclarationProductType } from 'types/content-declaration/EPDContentDeclarationProductType';
import { EPDProductType } from 'types/types';
import { YES_NO } from '../../constants';
import { useContentDeclarationTerminologyHelper } from '../../hooks/useContentDeclarationTerminologyHelper';
import {
  columnTotal,
  getBiogenicCarbonWeightHeaderPdf,
  getBiogenicCO2WeightHeaderPdf,
  numericCellBody,
} from '../GridService';
import { DataTableStyled } from '../StyledGrid';

const header = (
  <div className="flex flex-wrap align-items-center justify-content-between gap-2">
    <HeaderCaptionSemiBold className="white-space-nowrap">Product content</HeaderCaptionSemiBold>
  </div>
);

const ProductComponentsGridPdf: React.FunctionComponent<{
  epdVersionId: string;
  serviceIncludesProductInSystemBoundary?: string;
  contentDeclarationType: EPDContentDeclarationProductType;
  isISO21930StandardSelected?: boolean;
  productType?: EPDProductType;
  className?: string;
}> = ({
  epdVersionId,
  serviceIncludesProductInSystemBoundary,
  contentDeclarationType,
  isISO21930StandardSelected,
  productType,
  className,
}) => {
  const { data: rows, isFetching: isRowsFetching } = useEpdProductComponents(epdVersionId!, contentDeclarationType);
  const { data: lcaSpecification, isFetching: isLcaFetching } = useGetLCASpecification(epdVersionId!);
  const { replaceInString, replaceInNode } = useContentDeclarationTerminologyHelper();

  if (isRowsFetching || isLcaFetching) return null;

  if (
    (!rows || rows.length === 0) &&
    (contentDeclarationType !== EPDContentDeclarationProductType.Average ||
      (serviceIncludesProductInSystemBoundary !== YES_NO.NO && productType === EPDProductType.Services))
  )
    return null;

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column className="p-row-total" footer="Total" />
        <Column className="p-row-total" footer={columnTotal('weight', rows)} />
        <Column className="p-row-total" footer={columnTotal('finalWeightPercent', rows)} />
        <Column className="p-row-total" footer={columnTotal('biogenicWeightPercent', rows)} />
        <Column className="p-row-total" footer={columnTotal('biogenicCarbonWeight', rows)} />
        {isISO21930StandardSelected && <Column className="p-row-total" footer={columnTotal('biogenicCO2Weight', rows)} />}
      </Row>
      <Row style={{ borderTop: 'none' }}>
        <Column className="p-row-note" footer={<div>Note 1</div>} />
        <Column
          className="p-row-note"
          footer={
            <>
              1 kg biogenic carbon is equivalent to 44/12 kg of CO<sub>2</sub>
            </>
          }
          colSpan={isISO21930StandardSelected ? 5 : 4}
        />
      </Row>
    </ColumnGroup>
  );

  const isTableFilledIn = rows?.every((r) => r.weight) && rows?.every((r) => r.name);
  if (serviceIncludesProductInSystemBoundary === YES_NO.NO) {
    if (!rows?.some(() => true) || !isTableFilledIn) return null;
  }

  return (
    <DataTableStyled
      className={className}
      header={header}
      footerColumnGroup={footerGroup}
      value={rows}
      dataKey="id"
      tableStyle={{ minWidth: '49rem' }}
      style={{ overflow: 'auto', display: 'grid' }}
      showGridlines
    >
      <Column field="name" header="Content name" />
      <Column field="weight" header={replaceInString('Weight, kg')} body={numericCellBody()} />
      <Column
        field="finalWeightPercent"
        header={replaceInString('Post-consumer recycled material, weight-% of product')}
        body={numericCellBody()}
      />
      <Column
        field="biogenicWeightPercent"
        header={replaceInString('Biogenic material, weight-% of product')}
        body={numericCellBody()}
      />
      <Column
        field="biogenicCarbonWeight"
        header={replaceInNode(getBiogenicCarbonWeightHeaderPdf(lcaSpecification?.epdBasedOnUnit))}
        body={numericCellBody()}
      />
      {isISO21930StandardSelected && (
        <Column
          field="biogenicCO2Weight"
          header={replaceInNode(getBiogenicCO2WeightHeaderPdf(lcaSpecification?.epdBasedOnUnit))}
          body={numericCellBody()}
        />
      )}
    </DataTableStyled>
  );
};

export default ProductComponentsGridPdf;
